/**
 * "Limites de gasto" in Acompanhar › Uso de IA (docs/refactor/51, §6.3): "avisar quando passar de US$ 20 no dia; parar
 * em US$ 50" — for the whole organization, one project, one agent or one person. Each limit shows what its scope spent
 * this day or month against it; past the warning the inbox says so, and at the stop new work there is refused until
 * the period turns. A manager's screen, like the rest of Uso de IA.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatCost } from '../lib/format'
import type { SpendLimit } from '../lib/types'
import { ErrorBanner, Panel } from './primitives'

/** The agents a limit can name, by the provider their usage is reported under. */
const AGENTS = [
  { key: 'claude-code', label: 'Claude Code' },
  { key: 'codex', label: 'Codex' },
  { key: 'opencode', label: 'IA local (OpenCode)' },
]

export function SpendLimits() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { data: limits } = useQuery({ queryKey: ['spend-limits'], queryFn: api.usage.limits, retry: false })
  const { data: projects } = useQuery({ queryKey: ['repository-projects'], queryFn: api.repositories.projects, staleTime: 60_000 })
  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers, staleTime: 60_000 })
  const [scope, setScope] = useState<SpendLimit['scope']>('Organization')
  const [target, setTarget] = useState('')
  const [period, setPeriod] = useState<SpendLimit['period']>('Day')
  const [alert, setAlert] = useState('')
  const [stop, setStop] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['spend-limits'] })

  const amount = (text: string) => (text.trim() ? Number(text.replace(',', '.')) : null)
  const create = useMutation({
    mutationFn: () =>
      api.usage.createLimit({
        scope,
        targetId: scope === 'Project' || scope === 'Developer' ? target || null : null,
        targetKey: scope === 'Agent' ? target || null : null,
        period,
        alertUsd: amount(alert),
        stopUsd: amount(stop),
      }),
    onSuccess: () => {
      setAlert('')
      setStop('')
      setProblem(null)
      refresh()
    },
    onError: (error) => setProblem(error instanceof ApiError && error.code ? t(`spend.error.${error.code}`, {}) : t('error.generic')),
  })
  const remove = useMutation({ mutationFn: (id: string) => api.usage.deleteLimit(id), onSuccess: refresh })

  // A server from before has no limits: nothing to show rather than an error.
  if (limits === undefined) return null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate()
  }
  const targets =
    scope === 'Project' ? (projects ?? []).map((p) => ({ value: p.id, label: p.name }))
      : scope === 'Developer' ? (developers ?? []).filter((d) => d.isActive).map((d) => ({ value: d.id, label: d.displayName }))
        : scope === 'Agent' ? AGENTS.map((a) => ({ value: a.key, label: a.label }))
          : []

  return (
    <Panel title={t('spend.title')} subtitle={t('spend.hint')}>
      <div className="stack">
        {limits.length > 0 && (
          <ul className="spend-list">
            {limits.map((limit) => {
              const ceiling = limit.stopUsd ?? limit.alertUsd ?? 1
              const share = Math.min(100, Math.round((limit.spent / ceiling) * 100))
              return (
                <li key={limit.id} className={`spend-row${limit.stopped ? ' is-stopped' : limit.alerted ? ' is-alerted' : ''}`}>
                  <div className="row row--between row--wrap">
                    <strong className="small">
                      {limit.scope === 'Organization' ? t('spend.scope.Organization') : limit.target} · {t(`spend.period.${limit.period}`)}
                    </strong>
                    <span className="small">
                      {formatCost(limit.spent, locale)}
                      {limit.alertUsd != null && <span className="muted"> · {t('spend.alertAt', { amount: formatCost(limit.alertUsd, locale) })}</span>}
                      {limit.stopUsd != null && <span className="muted"> · {t('spend.stopAt', { amount: formatCost(limit.stopUsd, locale) })}</span>}
                    </span>
                    <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => remove.mutate(limit.id)} aria-label={t('spend.remove')} title={t('spend.remove')}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="spend-bar" role="progressbar" aria-valuenow={share} aria-valuemin={0} aria-valuemax={100}>
                    <span style={{ width: `${share}%` }} />
                  </div>
                  {limit.stopped && <span className="tiny">{t('spend.stopped')}</span>}
                </li>
              )
            })}
          </ul>
        )}

        <form className="row row--wrap spend-form" onSubmit={submit}>
          <select className="select select--sm" value={scope} onChange={(e) => { setScope(e.target.value as SpendLimit['scope']); setTarget('') }} aria-label={t('spend.scope')}>
            {(['Organization', 'Project', 'Agent', 'Developer'] as const).map((s) => <option key={s} value={s}>{t(`spend.scope.${s}`)}</option>)}
          </select>
          {targets.length > 0 && (
            <select className="select select--sm" value={target} onChange={(e) => setTarget(e.target.value)} aria-label={t('spend.target')}>
              <option value="">{t('spend.choose')}</option>
              {targets.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <select className="select select--sm" value={period} onChange={(e) => setPeriod(e.target.value as SpendLimit['period'])} aria-label={t('spend.periodLabel')}>
            <option value="Day">{t('spend.period.Day')}</option>
            <option value="Month">{t('spend.period.Month')}</option>
          </select>
          <label className="row row--tight small">
            {t('spend.alertLabel')}
            <input className="input input--sm spend-amount" inputMode="decimal" value={alert} onChange={(e) => setAlert(e.target.value)} placeholder="20" />
          </label>
          <label className="row row--tight small">
            {t('spend.stopLabel')}
            <input className="input input--sm spend-amount" inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} placeholder="50" />
          </label>
          <button type="submit" className="btn btn--sm btn--primary" disabled={create.isPending || (!alert.trim() && !stop.trim()) || (scope !== 'Organization' && !target)}>
            {t('spend.add')}
          </button>
        </form>
        {problem && <ErrorBanner message={problem} />}
      </div>
    </Panel>
  )
}
